/**
 * Engine boot: load the emscripten module, build its filesystem, run main().
 *
 * Supports both Fallout 1 (fallout1-ce) and Fallout 2 (fallout2-ce). The game
 * type determines the engine binary, home directory, and config file.
 *
 * Filesystem layout inside the module (FO2 shown; FO1 uses /fallout1-ce/):
 *
 *   /fallout2-ce/                MEMFS  — rebuilt from the vault on every boot
 *     master.dat, critter.dat, patch000.dat
 *     fallout2.cfg, f2_res.ini   written by us, read back on syncfs
 *     data/                      MEMFS  — imported patches, music, protos
 *       art/intrface/            MEMFS  — shell-shipped interface side-panel art
 *       MAPS/                    MEMFS  — per-session scratch, like a desktop run
 *       SAVEGAME/                IDBFS  — the only durable, engine-written path
 *
 * The engine already calls FS.syncfs() itself after a save and after writing
 * its config (see engine loadsave.cc and gconfig.cc), so persistence needs no
 * engine patch. We wrap FS.syncfs to piggyback on those calls: config snapshots
 * and save-list refreshes happen exactly when the engine says the world changed.
 */

import type { GameType } from './config';
import { cfgFileNameFor, homeDir, saveDir } from './config';
import { getFile, listFiles, setMeta } from './store';
import type { BridgeExports } from './saveedit/bridge';

export interface EmFS {
  mkdir(path: string, mode?: number): void;
  mkdirTree(path: string, mode?: number): void;
  chdir(path: string): void;
  writeFile(path: string, data: Uint8Array | string, opts?: object): void;
  readFile(path: string, opts?: { encoding?: 'binary' | 'utf8' }): Uint8Array | string;
  readdir(path: string): string[];
  stat(path: string): { size: number; mode: number; mtime: Date };
  unlink(path: string): void;
  rmdir(path: string): void;
  rename(oldPath: string, newPath: string): void;
  isDir(mode: number): boolean;
  analyzePath(path: string): { exists: boolean; object?: unknown };
  mount(type: unknown, opts: object, mountpoint: string): void;
  syncfs(populate: boolean, cb: (err: unknown) => void): void;
  syncfs(cb: (err: unknown) => void): void;
  filesystems: { IDBFS: unknown; MEMFS: unknown };
  /** Returns an FS.FSStream; `.fd` is what the fd-taking calls want. */
  open?(path: string, flags: string, mode?: number): { fd: number };
  write?(stream: { fd: number }, buffer: Uint8Array, offset: number, length: number, position?: number): number;
  /** Ranged read. The save editor's DAT reader uses this rather than `readFile`,
   *  which would copy a 333MB master.dat into the JS heap to fetch a proto. */
  read?(stream: { fd: number }, buffer: Uint8Array, offset: number, length: number, position?: number): number;
  close?(stream: { fd: number }): void;
  /** POSIX-shaped: takes a file descriptor, not a stream. Passing the stream throws EBADF. */
  ftruncate?(fd: number, len: number): void;
}

/**
 * The module object emscripten hands back.
 *
 * The `_rr_*` members are the save-editor debug bridge (engine `b769f51e`),
 * landed on the module by `EMSCRIPTEN_KEEPALIVE`. They are optional because a
 * previously vendored engine does not have them, and the shell degrades to a
 * disabled Inventory tab rather than failing to boot — see
 * `saveedit/bridge.ts`, which is the only place that reads them.
 */
export interface EmModule extends BridgeExports {
  FS: EmFS;
  callMain(args?: string[]): void;
  canvas?: HTMLCanvasElement;
  ENV?: Record<string, string>;
  _rr_resize_request?: (width: number, height: number) => number;
  _rr_resize_status?: () => number;
  _rr_resize_width?: () => number;
  _rr_resize_height?: () => number;
  _rr_game_active?: () => number;
  _rr_resize_presented?: () => number;
  _rr_pointer_event?: (normalizedX: number, normalizedY: number, buttons: number) => number;
  _rr_touch_event?: (normalizedX: number, normalizedY: number, pointerId: number, phase: number, timestamp: number) => number;
  _rr_touch_cancel_all?: () => number;
  _rr_touch_suppress_taps?: (milliseconds: number) => number;
  _rr_touch_pan_threshold?: (gamePixels: number) => number;
  _rr_touch_preview_kind?: () => number;
  _rr_touch_preview_target_x?: () => number;
  _rr_touch_preview_target_y?: () => number;
  _rr_touch_preview_path_count?: () => number;
  _rr_touch_preview_path_x?: (index: number) => number;
  _rr_touch_preview_path_y?: (index: number) => number;
  _rr_touch_preview_action_points?: () => number;
  _rr_touch_assist_scale?: (percent: number) => number;
  _rr_input_modality?: (touchLike: number) => number;
  _rr_in_combat?: () => number;
  _rr_indicator_count?: () => number;
  _rr_interface_bar_visible?: () => number;
  _rr_portrait_mode?: () => number;
  // The map cursor's current persistent mode, as the engine's GameMouseMode
  // enum: 0 move, 1 arrow, 2 crosshair, 3 use-item-on, and 4-10 the skilldex
  // skills. A phone has no floating cursor, so the shell reads this to show
  // the active mode as a badge instead.
  _rr_mouse_mode?: () => number;
  _rr_navigation_event?: (target: number) => number;
  // Set while an engine text prompt (save name, character name) is reading
  // keys, with the field's initial contents as a NUL-terminated C string. The
  // shell uses these to raise a phone keyboard — see `ui/textentry.ts`.
  _rr_text_input_active?: () => number;
  _rr_text_input_prefill?: () => number;
  // Enqueues one logical GNW key for the open prompt to read: printable ASCII
  // 32..122 as itself, 8 Backspace, 13 Return, 27 Escape.
  _rr_key_event?: (logicalKey: number) => number;
  _rr_arena_enemy_request?: (enemy: number) => number;
  _rr_arena_enemy_status?: () => number;
  _rr_arena_weapon_request?: (pid: number) => number;
  _rr_arena_weapon_status?: () => number;
  _rr_arena_ammo_request?: (pid: number) => number;
  _rr_arena_ammo_status?: () => number;
  _rr_arena_armor_request?: (pid: number) => number;
  _rr_arena_armor_status?: () => number;
  _rr_arena_weapon_skill?: () => number;
  _rr_arena_current_skill_value?: () => number;
  _rr_arena_target_hp?: () => number;
  _rr_arena_action_points?: () => number;
  _rr_arena_skill_request?: (value: number) => number;
  _rr_arena_skill_status?: () => number;
  _rr_arena_skill_value?: () => number;
  _rr_arena_range_request?: (distance: number) => number;
  _rr_arena_range_status?: () => number;
  _rr_arena_hit_chance?: () => number;
  _rr_arena_enable?: (enabled: number) => number;
  _rr_arena_critical_chance?: () => number;
  _rr_arena_attack_ap?: () => number;
  _rr_arena_light_request?: (daytime: number) => number;
  _rr_arena_light_status?: () => number;
  _rr_arena_is_day?: () => number;
  _rr_arena_special_request?: (stat: number, value: number) => number;
  _rr_arena_special_status?: () => number;
  _rr_arena_special_value?: (stat: number) => number;
  _rr_arena_traits_request?: (trait1: number, trait2: number) => number;
  _rr_arena_traits_status?: () => number;
  _rr_arena_trait_value?: (slot: number) => number;
  _rr_arena_perk_request?: (perk: number, enabled: number) => number;
  _rr_arena_perk_status?: () => number;
  _rr_arena_perk_rank?: (perk: number) => number;
  _rr_arena_sim_request?: (trials: number, hitLocation: number) => number;
  _rr_arena_sim_status?: () => number;
  _rr_arena_sim_trials?: () => number;
  _rr_arena_sim_hits?: () => number;
  _rr_arena_sim_criticals?: () => number;
  _rr_arena_sim_damage_milli?: () => number;
  _rr_arena_sim_damage_p50?: () => number;
  _rr_arena_sim_damage_p90?: () => number;
  _rr_arena_sim_damage_max?: () => number;
  _rr_arena_sim_knockdowns?: () => number;
  _rr_arena_sim_cripples?: () => number;
  _rr_arena_sim_bypasses?: () => number;
  _rr_arena_sim_kills?: () => number;
  _rr_arena_sim_attack_ap?: () => number;
  _rr_arena_rank_skill?: (skill: number, value: number) => number;
  _rr_arena_rank_request?: (weaponPid: number, ammoPid: number, trials: number, hitLocation: number) => number;
  _rr_arena_rank_status?: () => number;
  _rr_character_editor_active?: () => number;
  _rr_character_skill_index?: () => number;
  _rr_character_skill_value?: () => number;
  _rr_character_unspent_skill_points?: () => number;
  _rr_character_skill_adjust?: (delta: number) => number;
  _rr_debug_hitbox_count?: () => number;
  _rr_debug_hitbox_left?: (index: number) => number;
  _rr_debug_hitbox_top?: (index: number) => number;
  _rr_debug_hitbox_right?: (index: number) => number;
  _rr_debug_hitbox_bottom?: (index: number) => number;
  _rr_debug_hitbox_is_interface?: (index: number) => number;
  _rr_debug_touch_sequence?: () => number;
  _rr_debug_touch_type?: () => number;
  _rr_debug_touch_state?: () => number;
  _rr_debug_touch_x?: () => number;
  _rr_debug_touch_y?: () => number;
  _rr_debug_touch_start_x?: () => number;
  _rr_debug_touch_start_y?: () => number;
  _rr_debug_tap_sequence?: () => number;
  _rr_debug_tap_stages?: () => number;
  _rr_debug_tap_start_x?: () => number;
  _rr_debug_tap_start_y?: () => number;
  _rr_debug_tap_target_x?: () => number;
  _rr_debug_tap_target_y?: () => number;
  _rr_debug_tap_window?: () => number;
  _rr_debug_tap_button?: () => number;
  _rr_debug_tap_event?: () => number;
  _rr_debug_scroll_sequence?: () => number;
  _rr_debug_scroll_up_key?: () => number;
  _rr_debug_scroll_down_key?: () => number;
  _rr_debug_scroll_before?: () => number;
  _rr_debug_scroll_after?: () => number;
  _rr_debug_scroll_lane?: () => number;
  _rr_debug_scroll_phase?: () => number;
  _rr_debug_scroll_queued?: () => number;
  _rr_debug_scroll_consumed?: () => number;
  _rr_debug_scroll_plain_emits?: () => number;
  _rr_debug_perk_arrows_consumed?: () => number;
}

type ModuleFactory = (cfg: Record<string, unknown>) => Promise<EmModule>;

declare global {
  interface Window {
    fallout1ce?: ModuleFactory;
    fallout2ce?: ModuleFactory;
  }
}

const ENGINE_DEFS: Record<GameType, { js: string; global: keyof Pick<Window, 'fallout1ce' | 'fallout2ce'> }> = {
  fo1: { js: '/engine/fallout1-ce.js.js', global: 'fallout1ce' },
  fo2: { js: '/engine/fallout2-ce.js.js', global: 'fallout2ce' },
};

const scriptPromises: Partial<Record<GameType, Promise<ModuleFactory>>> = {};
function loadEngineScript(game: GameType): Promise<ModuleFactory> {
  if (scriptPromises[game]) return scriptPromises[game]!;
  const def = ENGINE_DEFS[game];
  scriptPromises[game] = new Promise((resolve, reject) => {
    if (window[def.global]) return resolve(window[def.global]!);
    const s = document.createElement('script');
    s.src = def.js;
    s.onload = () =>
      window[def.global]
        ? resolve(window[def.global]!)
        : reject(new Error(`engine script loaded but did not define ${def.global}`));
    s.onerror = () => reject(new Error(`failed to load ${def.js}`));
    document.head.appendChild(s);
  });
  return scriptPromises[game]!;
}

/**
 * SDL builds its WebGL context without preserveDrawingBuffer, which makes the
 * canvas read back blank for toDataURL and — critically — for captureStream.
 * Forcing the flag on is the price of the replay buffer. It costs a copy per
 * frame at 640x480, which is noise next to the engine's own blit.
 */
export function enableCanvasReadback(): void {
  const proto = HTMLCanvasElement.prototype as unknown as {
    getContext: (type: string, attrs?: object) => unknown;
    __f2cePatched?: boolean;
  };
  if (proto.__f2cePatched) return;
  const orig = proto.getContext;
  proto.getContext = function (type: string, attrs?: object) {
    if (String(type).startsWith('webgl')) attrs = { ...(attrs || {}), preserveDrawingBuffer: true };
    return orig.call(this, type, attrs);
  };
  proto.__f2cePatched = true;
}

export interface BootProgress {
  (stage: string, done: number, total: number): void;
}

/**
 * Where the immutable half of the install is read from. The vault is the
 * product; `dev/gamedata.ts` substitutes the dev endpoint so engine work does
 * not have to go through an import first.
 */
export type GameFileData = Blob | ReadableStream<Uint8Array>;

export interface GameDataSource {
  list(): Promise<{ path: string; size: number }[]>;
  open(path: string): Promise<GameFileData | null>;
}

const vaultSource: GameDataSource = {
  list: () => listFiles(),
  open: async (path) => (await getFile(path))?.blob ?? null,
};

export interface BootOptions {
  canvas: HTMLCanvasElement;
  gameType: GameType;
  /** Defaults to the vault. */
  source?: GameDataSource;
  /** Full config text — fallout2.cfg for FO2, fallout.cfg for FO1. */
  cfgText: string;
  /** Legacy HRP config kept in sync for imported high-resolution art packs. FO2-only. */
  f2ResText?: string;
  /** Resolution config for FO1: sets WINDOWED=1 so SDL doesn't use fullscreen. */
  f1ResText?: string;
  onProgress: BootProgress;
  onLog?: (line: string) => void;
  onSync?: () => void;
  onExit?: (code: number) => void;
  /**
   * Runs after the filesystem is fully built and before main() — the window
   * where the Mod Maker compiles enabled recipes into MEMFS. Must not throw;
   * a failure here is the hook's problem to report, never a boot failure.
   */
  beforeMain?: (FS: EmFS) => Promise<void>;
  /** Optional argv passed to the CE executable. Used by the dev arena to load
   *  its staged slot through the engine's normal --dev-load-game path. */
  mainArgs?: string[];
}

/**
 * Emscripten's ErrnoError carries a number and nothing else — no stack, no
 * path. Annotating each filesystem phase is the difference between "errno=8"
 * and a bug you can fix.
 */
async function step<T>(label: string, fn: () => T | Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    const err = e as { errno?: number; message?: string; name?: string };
    const detail = err?.errno !== undefined ? `errno=${err.errno}` : err?.message || String(e);
    throw new Error(`${label}: ${err?.name ?? 'Error'} ${detail}`, { cause: e });
  }
}

/**
 * Streams a game file into MEMFS without ever holding the whole thing in JS
 * heap. Takes a Blob (the vault) or a raw byte stream (the dev endpoint, which
 * never materialises a Blob at all).
 */
async function writeToFs(FS: EmFS, path: string, data: GameFileData, size: number): Promise<void> {
  const chunked = typeof FS.open === 'function' && typeof FS.write === 'function' && typeof FS.close === 'function';
  if (data instanceof Blob && (!chunked || data.size < 8 * 1024 * 1024)) {
    FS.writeFile(path, new Uint8Array(await data.arrayBuffer()));
    return;
  }
  const stream = FS.open!(path, 'w+');
  try {
    // Preallocate: MEMFS grows its backing array by ~1.125x per overflow, so an
    // unsized 333MB sequential write reallocates and copies dozens of times.
    FS.ftruncate?.(stream.fd, size);
    const reader = (data instanceof Blob ? (data.stream() as ReadableStream<Uint8Array>) : data).getReader();
    let pos = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value?.length) continue;
      FS.write!(stream, value, 0, value.length, pos);
      pos += value.length;
    }
    // A stream that ends early leaves a preallocated tail of zeros, which the
    // engine would read as a valid-but-empty archive. Say so instead.
    if (pos !== size) throw new Error(`short read: ${pos} of ${size} bytes`);
  } finally {
    FS.close!(stream);
  }
}

function mkdirp(FS: EmFS, path: string): void {
  if (typeof FS.mkdirTree === 'function') {
    FS.mkdirTree(path);
    return;
  }
  const parts = path.split('/').filter(Boolean);
  let cur = '';
  for (const p of parts) {
    cur += '/' + p;
    try {
      FS.mkdir(cur);
    } catch {
      /* exists */
    }
  }
}

/**
 * Interface side-panel art (Hi-Res Patch 4.1.8 by Mash).
 *
 * The engine already wants these: `settings.ui.iface_bar_side_art` defaults to
 * 2, and `interface.cc: sidePanelsInit()` draws
 * `art\intrface\HR_IFACELFT<n>.frm` / `HR_IFACERHT<n>.frm` into the two windows
 * either side of the fixed 640-wide interface bar whenever the screen is wider
 * than the bar. Nothing needs configuring — the files just have to resolve.
 *
 * Where they have to land: `game.cc: gameDbInit()` builds the search chain in
 * reverse priority, so `master_patches` (our `data/`, a *directory* xbase — see
 * `xfile.cc: xbaseOpen()`, which falls back to `compat_is_dir`) sits at the head
 * and is consulted before every .dat. `xfileOpen()` joins the xbase path with
 * the requested relative path, so `art\intrface\HR_IFACELFT2.frm` resolves to
 * `<HOME>/data/art/intrface/HR_IFACELFT2.frm`. (`compat_resolve_path` walks
 * each component case-insensitively, so the on-disk case is not load-bearing.)
 *
 * Consequence worth stating: a player who imports their own hi-res art pack as
 * loose files under `data/art/intrface/` would have these two shadowed — we
 * write them after the vault is mounted, so ours win. They are the identical
 * files from the same patch, so the outcome is the same picture. An imported
 * `f2_res.dat` is a DAT further down the chain and is shadowed too, likewise
 * with identical content. Accepted.
 *
 * We deliberately do NOT ship `hr_iface_800.frm` (the widened interface bar
 * itself): it is built from Interplay's own art. These two wings are Mash's.
 *
 * Non-fatal by construction. No wings is a cosmetic loss; a failed boot is not.
 */
const SIDE_PANEL_ART = ['HR_IFACELFT2.frm', 'HR_IFACERHT2.frm'];

async function installSidePanelArt(FS: EmFS, home: string, log?: (line: string) => void): Promise<void> {
  const dir = `${home}/data/art/intrface`;
  try {
    const files = await Promise.all(
      SIDE_PANEL_ART.map(async (name) => {
        const res = await fetch(`/art/intrface/${name}`);
        if (!res.ok) throw new Error(`${name}: HTTP ${res.status}`);
        return { name, bytes: new Uint8Array(await res.arrayBuffer()) };
      })
    );
    mkdirp(FS, dir);
    for (const f of files) FS.writeFile(`${dir}/${f.name}`, f.bytes);
    log?.(`[shell] interface side-panel art installed (${SIDE_PANEL_ART.join(', ')})`);
  } catch (e) {
    log?.(`[shell] side-panel art unavailable, continuing without wings: ${(e as Error)?.message ?? e}`);
  }
}

export interface BootedEngine {
  module: EmModule;
  FS: EmFS;
  sync(): Promise<void>;
}

export async function boot(opts: BootOptions): Promise<BootedEngine> {
  const { canvas, cfgText, gameType, onProgress } = opts;
  const HOME = homeDir(gameType);
  const SAVE_DIR = saveDir(gameType);
  const cfgFileName = cfgFileNameFor(gameType);

  onProgress('Loading engine', 0, 1);
  const factory = await loadEngineScript(gameType);

  let mod: EmModule;
  const cfg: Record<string, unknown> = {
    canvas,
    noInitialRun: true,
    print: (l: string) => opts.onLog?.(l),
    printErr: (l: string) => opts.onLog?.(l),
    preInit: [
      () => {
        const m = mod as unknown as { ENV: Record<string, string> };
        if (m.ENV) Object.assign(m.ENV, { HOME });
      },
    ],
    onExit: (code: number) => opts.onExit?.(code),
    setStatus: (s: string) => {
      if (s) opts.onLog?.('[status] ' + s);
    },
  };

  // Emscripten mutates and returns the object it is handed, so `mod` and the
  // resolved instance are the same object — preInit needs it before the await.
  mod = cfg as unknown as EmModule;
  const inst = await factory(cfg);
  const FS = inst.FS;

  // --- MEMFS: the immutable half of the install -----------------------------
  await step('create home', () => {
    mkdirp(FS, HOME);
    FS.chdir(HOME);
    FS.writeFile(`${HOME}/${cfgFileName}`, cfgText);
    if (gameType === 'fo2' && opts.f2ResText) {
      FS.writeFile(`${HOME}/f2_res.ini`, opts.f2ResText);
    }
    if (gameType === 'fo1' && opts.f1ResText) {
      FS.writeFile(`${HOME}/f1_res.ini`, opts.f1ResText);
    }
  });

  const source = opts.source ?? vaultSource;
  const files = await source.list();
  const total = files.reduce((n, f) => n + f.size, 0);
  let done = 0;
  onProgress('Mounting game data', 0, total);
  for (const f of files) {
    const blob = await source.open(f.path);
    if (!blob) continue;
    const dir = f.path.includes('/') ? `${HOME}/${f.path.slice(0, f.path.lastIndexOf('/'))}` : HOME;
    await step(`write ${f.path}`, async () => {
      mkdirp(FS, dir);
      await writeToFs(FS, `${HOME}/${f.path}`, blob, f.size);
    });
    done += f.size;
    onProgress('Mounting game data', done, total);
  }

  await step('create scratch dirs', () => {
    mkdirp(FS, `${HOME}/data`);
    mkdirp(FS, `${HOME}/data/MAPS`);
    mkdirp(FS, `${HOME}/data/proto/critters`);
    mkdirp(FS, `${HOME}/data/proto/items`);
  });

  // After the vault, so ours are the copies the engine sees (see the note on
  // installSidePanelArt). FO2-only; FO1 has no Hi-Res patch art. Never throws.
  if (gameType === 'fo2') {
    await installSidePanelArt(FS, HOME, opts.onLog);
  }

  // --- IDBFS: the mutable half ---------------------------------------------
  onProgress('Restoring saves', 0, 1);
  await step('mount IDBFS', () => {
    mkdirp(FS, SAVE_DIR);
    FS.mount(FS.filesystems.IDBFS, {}, SAVE_DIR);
  });
  await step('restore saves', () =>
    new Promise<void>((resolve, reject) => FS.syncfs(true, (err) => (err ? reject(err) : resolve())))
  );
  onProgress('Restoring saves', 1, 1);

  // The engine calls the global FS.syncfs() after saves and config writes.
  // Wrapping it turns those into our persistence + UI-refresh hook.
  const rawSyncfs = FS.syncfs.bind(FS) as (a: unknown, b?: unknown) => void;
  let syncTail: Promise<void> = Promise.resolve();
  const enqueueSync = (populate: boolean): Promise<void> => {
    const run = syncTail.then(() => new Promise<void>((resolve, reject) => {
      rawSyncfs(populate, (err: unknown) => (err ? reject(err) : resolve()));
    }));
    syncTail = run.catch(() => {});
    return run;
  };
  const afterSync = async () => {
    try {
      const savedCfg = FS.readFile(`${HOME}/${cfgFileName}`, { encoding: 'utf8' }) as string;
      await setMeta(cfgFileName, savedCfg);
    } catch {
      /* config not written yet */
    }
    opts.onSync?.();
  };
  (FS as unknown as { syncfs: unknown }).syncfs = (a: unknown, b?: unknown) => {
    const cb = (typeof a === 'function' ? a : b) as (err: unknown) => void;
    const populate = typeof a === 'boolean' ? a : false;
    void enqueueSync(populate)
      .then(async () => {
        if (!populate) await afterSync();
        cb?.(null);
      })
      .catch((err) => cb?.(err));
  };

  const sync = async () => {
    await enqueueSync(false);
    await afterSync();
  };

  if (opts.beforeMain) {
    onProgress('Applying recipes', 0, 1);
    try {
      await opts.beforeMain(FS);
    } catch (e) {
      opts.onLog?.(`[beforeMain] ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  onProgress('Starting', 1, 1);
  inst.callMain(opts.mainArgs);
  return { module: inst, FS, sync };
}
